import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Mic, X } from 'lucide-react';
import type { ChatMessage, InnerCircleGroup } from '../../types/inner-circle';
import { useProfile } from '../../contexts/ProfileContext';

interface GroupChatProps {
  group: InnerCircleGroup;
  messages: ChatMessage[];
  onSendMessage: (content: string, type?: ChatMessage['type'], fileUrl?: string) => void;
}

export function GroupChat({ group, messages, onSendMessage }: GroupChatProps) {
  const { profile } = useProfile();
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="flex flex-col h-full min-h-[400px] bg-[#2A2B2E] rounded-3xl">
      <div className="p-6 border-b border-gray-800">
        <h2 className="text-lg font-medium">{group.name}</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {messages.map((msg) => {
          const isOwn = msg.user_id === profile.id;
          return (
            <div
              key={msg.id}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'} max-w-full`}
            >
              <div
                className={`max-w-[85%] md:max-w-[70%] rounded-2xl p-3 ${
                  isOwn
                    ? 'bg-[var(--accent-color)] text-black ml-12'
                    : 'bg-black/20 mr-12'
                }`}
              >
                {!isOwn && (
                  <div className="text-sm text-gray-400 mb-1">
                    {msg.profile?.name || 'User'}
                  </div>
                )}
                {msg.type === 'text' && <p>{msg.content}</p>}
                {msg.type === 'file' && msg.file_url && (
                  <a
                    href={msg.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--accent-color)] hover:underline"
                  >
                    View Attachment
                  </a>
                )}
                <div
                  className={`text-xs mt-1 ${
                    isOwn ? 'text-black/60' : 'text-gray-400'
                  }`}
                >
                  {formatTime(msg.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-800">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="p-2 hover:bg-black/20 rounded-xl transition-colors"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => setIsRecording(!isRecording)}
            className={`p-2 rounded-xl transition-colors ${
              isRecording ? 'bg-red-500 text-white' : 'hover:bg-black/20'
            }`}
          >
            {isRecording ? <X className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-black/20 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
          />
          <button
            type="submit"
            disabled={!message.trim()}
            className="p-2 bg-[var(--accent-color)] text-black rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}